import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import { Icon } from '../../icons';
import { Badge, Btn, Card, FormRow, Field, Modal, SearchBar, FormActions } from '../../components/ui';
import { ConfirmModal } from '../../components/shared';
import ServiceProviderService from '../../api/serviceProvider';
import { toast } from 'react-hot-toast';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

const translations = { en, hi, gu };

const categoryIcons = {
  "Plumbing": "🔧",
  "Electrical": "⚡",
  "Painting": "🎨",
  "Carpentry": "🔨",
  "AC Repair": "❄️",
  "Pest Control": "🕷️",
  "Cleaning": "🧹",
  "Security": "🛡️",
  "Other": "🛠️"
};

export default function ServicesScreen() {
  const { language = 'en' } = useSettings();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  const isStrictMember = (user?.primary_role_label === 'Member' || user?.roles_list?.includes('member')) &&
    !user?.roles_list?.includes('super_admin') &&
    !user?.roles_list?.includes('admin');

  const canAdd = hasPermission('services.create') && !isStrictMember;
  const canEdit = hasPermission('services.update') && !isStrictMember;
  const canDelete = hasPermission('services.delete') && !isStrictMember;

  const t = (key, params) => {
    let str = translations[language]?.services?.[key] || translations.en?.services?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem] = useState(null);
  const [search, setSearch] = useState("");
  const def = { name: "", category: "", phone: "", alternate_phone: "", address: "", rating: "4.0", notes: "", verified: false, tags: "" };
  const [form, setForm] = useState(def);
  const [errors, setErrors] = useState({});
  const sf = k => e => {
    const val = e.target.value;
    setForm(f => ({ ...f, [k]: val }));
    if (errors[k]) {
      setErrors(prev => {
        const copy = { ...prev };
        delete copy[k];
        return copy;
      });
    }
  };
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  const validateForm = () => {
    const errs = {};
    const trimmedName = form.name?.trim() || "";
    if (!trimmedName) {
      errs.name = t("val_name_required") || "Name is required";
    } else if (/\d/.test(trimmedName)) {
      errs.name = t("val_name_invalid") || "Name must be a valid provider name and cannot contain numbers";
    } else if (!/^[\p{L}\s.'\-]+$/u.test(trimmedName)) {
      errs.name = t("val_name_invalid") || "Name must be a valid provider name and cannot contain numbers";
    }

    if (!form.category || !form.category.trim()) {
      errs.category = t("val_category_required") || "Category is required";
    }

    const trimmedPhone = form.phone?.trim() || "";
    if (!trimmedPhone) {
      errs.phone = t("val_phone_required") || "Phone number is required";
    } else {
      const cleanPhone = trimmedPhone.replace(/[\s\-()]/g, '');
      if (!/^\+?[0-9]{7,15}$/.test(cleanPhone)) {
        errs.phone = t("val_phone_invalid") || "Please enter a valid phone number (7-15 digits)";
      }
    }

    if (form.alternate_phone?.trim()) {
      const cleanAltPhone = form.alternate_phone.trim().replace(/[\s\-()]/g, '');
      if (!/^\+?[0-9]{7,15}$/.test(cleanAltPhone)) {
        errs.alternate_phone = t("val_alt_phone_invalid") || "Please enter a valid alternate phone number (7-15 digits)";
      }
    }

    const trimmedRating = String(form.rating ?? "").trim();
    if (!trimmedRating) {
      errs.rating = t("val_rating_range") || "Rating must be between 1 and 5";
    } else {
      const ratingVal = parseFloat(trimmedRating);
      if (isNaN(ratingVal) || ratingVal < 1 || ratingVal > 5) {
        errs.rating = t("val_rating_range") || "Rating must be between 1 and 5";
      }
    }

    return errs;
  };

  const handleBlur = k => () => {
    if (k === 'name') {
      const trimmedName = form.name?.trim() || "";
      if (!trimmedName) {
        setErrors(prev => ({ ...prev, name: t("val_name_required") || "Name is required" }));
      } else if (/\d/.test(trimmedName) || !/^[\p{L}\s.'\-]+$/u.test(trimmedName)) {
        setErrors(prev => ({ ...prev, name: t("val_name_invalid") || "Name must be a valid provider name and cannot contain numbers" }));
      }
    } else if (k === 'category') {
      if (!form.category || !form.category.trim()) {
        setErrors(prev => ({ ...prev, category: t("val_category_required") || "Category is required" }));
      }
    } else if (k === 'phone') {
      const trimmedPhone = form.phone?.trim() || "";
      if (!trimmedPhone) {
        setErrors(prev => ({ ...prev, phone: t("val_phone_required") || "Phone number is required" }));
      } else {
        const cleanPhone = trimmedPhone.replace(/[\s\-()]/g, '');
        if (!/^\+?[0-9]{7,15}$/.test(cleanPhone)) {
          setErrors(prev => ({ ...prev, phone: t("val_phone_invalid") || "Please enter a valid phone number (7-15 digits)" }));
        }
      }
    } else if (k === 'alternate_phone') {
      if (form.alternate_phone?.trim()) {
        const cleanAltPhone = form.alternate_phone.trim().replace(/[\s\-()]/g, '');
        if (!/^\+?[0-9]{7,15}$/.test(cleanAltPhone)) {
          setErrors(prev => ({ ...prev, alternate_phone: t("val_alt_phone_invalid") || "Please enter a valid alternate phone number (7-15 digits)" }));
        }
      }
    } else if (k === 'rating') {
      const trimmedRating = String(form.rating ?? "").trim();
      if (!trimmedRating) {
        setErrors(prev => ({ ...prev, rating: t("val_rating_range") || "Rating must be between 1 and 5" }));
      } else {
        const ratingVal = parseFloat(trimmedRating);
        if (isNaN(ratingVal) || ratingVal < 1 || ratingVal > 5) {
          setErrors(prev => ({ ...prev, rating: t("val_rating_range") || "Rating must be between 1 and 5" }));
        }
      }
    }
  };

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ServiceProviderService.getProviders(page, search);
      if (res.success && res.data) {
        setProviders(res.data);
        if (res.meta) {
          setPagination(res.meta);
        }
      }
    } catch (err) {
      toast.error(t("toast_load_failed"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  const lastFetch = useRef(null);
  useEffect(() => {
    const currentParams = JSON.stringify({ page, search });
    if (lastFetch.current === currentParams) return;
    lastFetch.current = currentParams;
    fetchProviders();
  }, [fetchProviders, page, search]);

  const save = async () => {
    if (saving) return;
    if ((editItem && !canEdit) || (!editItem && !canAdd)) {
      toast.error(t("toast_save_failed"), { id: 'service-provider-error' });
      return;
    }
    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstErrorMessage = errs.name || errs.category || errs.phone || errs.alternate_phone || errs.rating || Object.values(errs)[0];
      toast.error(firstErrorMessage, { id: 'service-provider-error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        phone: form.phone.trim(),
        alternate_phone: form.alternate_phone ? form.alternate_phone.trim() : null,
        address: form.address ? form.address.trim() : null,
        rating: parseFloat(form.rating) || 4.0,
        tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
        verified: form.verified,
        notes: form.notes ? form.notes.trim() : null,
        icon: categoryIcons[form.category] || "🛠️"
      };

      if (editItem) {
        await ServiceProviderService.updateProvider(editItem.id, payload);
        toast.success(t("toast_updated"), { id: 'service-provider-success' });
      } else {
        await ServiceProviderService.createProvider(payload);
        toast.success(t("toast_added"), { id: 'service-provider-success' });
      }
      setModal(false);
      setEditItem(null);
      setForm(def);
      setErrors({});
      fetchProviders();
    } catch (err) {
      console.error("Failed to save provider:", err);
      const apiErrors = err.errors || err.data?.errors || err.response?.data?.errors;
      if (apiErrors && typeof apiErrors === 'object') {
        const mappedErrors = {};
        Object.keys(apiErrors).forEach(key => {
          mappedErrors[key] = Array.isArray(apiErrors[key]) ? apiErrors[key][0] : String(apiErrors[key]);
        });
        setErrors(prev => ({ ...prev, ...mappedErrors }));
        const firstApiError = Object.values(mappedErrors)[0];
        toast.error(firstApiError || t("toast_save_failed"), { id: 'service-provider-error' });
      } else {
        const msg = err.message || err.data?.message || err.response?.data?.message || t("toast_save_failed");
        toast.error(msg, { id: 'service-provider-error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const startEdit = p => {
    if (!canEdit) return;
    setEditItem(p);
    setForm({
      name: p.name,
      category: p.category,
      phone: p.phone,
      alternate_phone: p.alternate_phone || "",
      address: p.address || "",
      rating: String(p.rating || 4.0),
      notes: p.notes || "",
      verified: p.verified || false,
      tags: p.tags && Array.isArray(p.tags) ? p.tags.join(", ") : ""
    });
    setErrors({});
    setModal(true);
  };

  const confirmDel = async () => {
    if (!canDelete) return;
    try {
      await ServiceProviderService.deleteProvider(delItem.id);
      toast.success(t("toast_deleted"));
      setDelItem(null);
      fetchProviders();
    } catch (err) {
      toast.error(t("toast_delete_failed"));
    }
  };

  // When search changes, reset to page 1
  useEffect(() => {
    setPage(1);
  }, [search]);

  return (
    <div className="fade-up space-y-4">
      {delItem && <ConfirmModal title={t("title_remove_provider")} message={t("msg_remove_provider", { name: delItem.name })} onConfirm={confirmDel} onCancel={() => setDelItem(null)} />}
      {modal && (
        <Modal title={editItem ? t("title_edit_provider") : t("title_add_provider")} onClose={() => { setModal(false); setEditItem(null); setErrors({}); }}>
          <FormRow cols={2}>
            <Field 
              label={t("label_name")} 
              value={form.name} 
              onChange={sf("name")} 
              onBlur={handleBlur("name")}
              error={errors.name}
              placeholder="e.g. Ramesh Kumar"
              required 
            />
            <Field 
              label={t("label_category")} 
              type="select" 
              value={form.category} 
              onChange={sf("category")} 
              onBlur={handleBlur("category")}
              error={errors.category}
              required 
              options={[
                { value: "", label: t("opt_select_category") || "Select Category" },
                { value: "Plumbing", label: t("cat_plumbing") },
                { value: "Electrical", label: t("cat_electrical") },
                { value: "Painting", label: t("cat_painting") },
                { value: "Carpentry", label: t("cat_carpentry") },
                { value: "AC Repair", label: t("cat_ac_repair") },
                { value: "Pest Control", label: t("cat_pest_control") },
                { value: "Cleaning", label: t("cat_cleaning") },
                { value: "Security", label: t("cat_security") },
                { value: "Other", label: t("cat_other") }
              ]} 
            />
          </FormRow>
          <FormRow cols={2}>
            <Field 
              label={t("label_phone")} 
              value={form.phone} 
              onChange={sf("phone")} 
              onBlur={handleBlur("phone")}
              error={errors.phone}
              placeholder="e.g. 9876543210"
              required 
            />
            <Field 
              label={t("label_alt_phone")} 
              value={form.alternate_phone} 
              onChange={sf("alternate_phone")} 
              onBlur={handleBlur("alternate_phone")}
              error={errors.alternate_phone}
              placeholder="Optional alternate number"
            />
          </FormRow>
          <FormRow>
            <Field label={t("label_address")} value={form.address} onChange={sf("address")} placeholder="e.g. Ahmadabad" />
          </FormRow>
          <FormRow cols={2}>
            <Field 
              label={t("label_rating")} 
              type="number" 
              value={form.rating} 
              onChange={sf("rating")} 
              onBlur={handleBlur("rating")}
              error={errors.rating}
              min="1" 
              max="5" 
              step="0.1" 
              placeholder="1.0 - 5.0"
            />
            <Field label={t("label_tags")} value={form.tags} onChange={sf("tags")} placeholder={t("placeholder_tags")} />
          </FormRow>
          <FormRow>
            <Field label={t("label_notes")} type="textarea" value={form.notes} onChange={sf("notes")} rows={2} />
          </FormRow>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-400 mb-3 cursor-pointer select-none">
            <input type="checkbox" checked={form.verified} onChange={e => setForm(f => ({ ...f, verified: e.target.checked }))} className="w-4 h-4" />
            {t("label_mark_verified")}
          </label>
          <FormActions onSave={save} onCancel={() => { setModal(false); setEditItem(null); setErrors({}); }} label={editItem ? t("btn_update_provider") : t("btn_add_provider")} loading={saving} disabled={saving} />
        </Modal>
      )}
      <div className="flex flex-wrap sm:flex-nowrap gap-3 items-center">
        <SearchBar value={search} onChange={setSearch} placeholder={t("placeholder_search")} className="w-full sm:w-72" />
        {canAdd && (
          <div className="hidden lg:block">
            <Btn sm variant="primary" icon="plus" onClick={() => { setEditItem(null); setForm(def); setErrors({}); setModal(true); }}>{t("btn_add_provider")}</Btn>
          </div>
        )}
        <div className="ml-auto text-xs text-muted whitespace-nowrap flex-shrink-0">
          {loading ? (
            <span className="w-16 h-4 bg-slate-200 rounded animate-pulse inline-block align-middle"></span>
          ) : (
            t("msg_providers_count", { count: pagination.total })
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {loading ? (
          Array(4).fill(0).map((_, i) => (
            <Card key={`skel-${i}`}>
              <div className="flex gap-4 animate-pulse">
                <div className="w-12 h-12 bg-slate-200 dark:bg-slate-700 rounded-xl flex-shrink-0" />
                <div className="flex-1 space-y-3 py-1">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3" />
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
                  <div className="flex gap-2 mt-2">
                    <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-16" />
                    <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-20" />
                  </div>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <>
            {/* Desktop View */}
            <div className="hidden lg:grid grid-cols-2 gap-4 col-span-full">
              {providers.map(p => (
                <Card key={p.id} hover>
                  <div className="flex gap-4">
                    <div className="text-3xl flex-shrink-0 w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-xl flex items-center justify-center">{p.icon || "🔧"}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-bold text-ink">{p.name}</span>
                        {p.verified && <Badge color="green" sm>{t("badge_verified")}</Badge>}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted mb-1.5">
                        <Badge color="blue" sm>{t("cat_" + (p.category || "").toLowerCase().replace(" ", "_")) || p.category}</Badge>
                        <span>⭐ {p.rating}</span>
                        <span>📍 {p.address || "-"}</span>
                      </div>
                      <div className="text-xs font-medium text-slate-700 dark:text-slate-400 mb-1.5">📞 {p.phone}{p.alternate_phone ? ` · ${p.alternate_phone}` : ""}</div>
                      <div className="flex gap-1 flex-wrap">{(p.tags || []).map(tag => <Badge key={tag} color={tag === "Emergency" ? "red" : "cyan"} sm>{t("tag_" + tag.toLowerCase()) || tag}</Badge>)}</div>
                    </div>
                    {(canEdit || canDelete) && (
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        {canEdit && <Btn xs icon="edit" onClick={() => startEdit(p)}>{t("btn_edit")}</Btn>}
                        {canDelete && <Btn xs variant="danger" icon="trash" onClick={() => setDelItem(p)}>{t("btn_del")}</Btn>}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            {/* Mobile View */}
            <div className="lg:hidden -mx-4 sm:-mx-6 col-span-full">
              {providers.map(p => (
                <IonItemSliding key={p.id} className="border-b border-slate-100 dark:border-slate-800/60 bg-white dark:bg-slate-900">
                  <IonItem className="--background:transparent --padding-start:16px --padding-end:16px --inner-padding-end:0">
                    <div className="py-3 w-full flex gap-3">
                      <div className="text-2xl flex-shrink-0 w-10 h-10 bg-slate-50 dark:bg-slate-800 rounded-xl flex items-center justify-center">{p.icon || "🔧"}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-bold text-ink">{p.name}</span>
                          {p.verified && <Badge color="green" sm>{t("badge_verified")}</Badge>}
                        </div>
                        <div className="text-xs text-muted mb-1 flex items-center gap-2">
                          <Badge color="blue" sm>{t("cat_" + (p.category || "").toLowerCase().replace(" ", "_")) || p.category}</Badge>
                          <span>⭐ {p.rating}</span>
                        </div>
                        <div className="text-xs font-medium text-slate-700 dark:text-slate-400">📞 {p.phone}</div>
                      </div>
                    </div>
                  </IonItem>
                  {(canEdit || canDelete) && (
                    <IonItemOptions side="end">
                      {canEdit && (
                        <IonItemOption color="primary" onClick={() => startEdit(p)}>
                          <div className="flex flex-col items-center justify-center w-full h-full px-2">
                            <Icon name="edit" size={20} className="mb-1" />
                            <span className="text-xs font-medium">{t("btn_edit")}</span>
                          </div>
                        </IonItemOption>
                      )}
                      {canDelete && (
                        <IonItemOption color="danger" onClick={() => setDelItem(p)}>
                          <div className="flex flex-col items-center justify-center w-full h-full px-2">
                            <Icon name="trash" size={20} className="mb-1" />
                            <span className="text-xs font-medium">{t("btn_del")}</span>
                          </div>
                        </IonItemOption>
                      )}
                    </IonItemOptions>
                  )}
                </IonItemSliding>
              ))}
            </div>
          </>
        )}
        {!loading && providers.length === 0 && (
          <div className="col-span-full py-12 text-center text-slate-500 dark:text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-800/60 rounded-xl">
            {t("msg_no_providers")}
          </div>
        )}
      </div>

      {pagination.last_page > 1 && (
        <div className="flex justify-between items-center mt-6 pt-4 border-t border-slate-100">
          <span className="text-sm text-slate-500">{t("msg_showing_page", { current: pagination.current_page, last: pagination.last_page })}</span>
          <div className="flex gap-2">
            <Btn sm variant="outline" disabled={pagination.current_page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>{t("btn_previous")}</Btn>
            <Btn sm variant="outline" disabled={pagination.current_page >= pagination.last_page} onClick={() => setPage(p => Math.min(pagination.last_page, p + 1))}>{t("btn_next")}</Btn>
          </div>
        </div>
      )}

      {/* FAB for Mobile */}
      {canAdd && typeof document !== 'undefined' && createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => { setEditItem(null); setForm(def); setErrors({}); setModal(true); }}
            className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer"
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
